import React, { useState, useEffect, useCallback } from 'react';
import {
  FaLock, FaPlayCircle, FaCheckCircle,
  FaStar, FaChevronDown, FaChevronUp, FaBook, FaVideo, FaUpload
} from 'react-icons/fa';
import VideoRecorder from '../../components/Student/VideoRecorder';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const LessonsPage = () => {
  const [lessons, setLessons] = useState([]);
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [lessonData, setLessonData] = useState({});
  const [loading, setLoading] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [recorderModal, setRecorderModal] = useState(null); // { video, lesson }

  const [selectedLessonForPayment, setSelectedLessonForPayment] = useState(null);
  const [utr, setUtr] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // { success: boolean, message: string }

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const handleLessonPaymentSubmit = async (e) => {
    e.preventDefault();
    if (!utr || utr.trim().length < 5) return;

    setPaymentLoading(true);
    setPaymentStatus(null);

    try {
      const res = await fetch(`${API_BASE}/payment/manual-upi/submit`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          utr: utr.trim(),
          courseName: selectedLessonForPayment.title,
          amount: selectedLessonForPayment.price * 100 // storing in cents/paise
        })
      });
      const data = await res.json();
      if (data.success) {
        setPaymentStatus({ success: true, message: 'Reference submitted! Please wait for admin approval.' });
        setUtr('');
        // Silent refresh to update UI if needed (though it will be pending)
        fetchLessons(true);
      } else {
        setPaymentStatus({ success: false, message: data.message || 'Submission failed.' });
      }
    } catch (err) {
      setPaymentStatus({ success: false, message: 'Server error. Please try again.' });
    } finally {
      setPaymentLoading(false);
    }
  };

  const fetchLessons = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/lessons`, { headers });
      const data = await res.json();
      if (data.success) setLessons(data.data);
    } catch (e) {
      console.error(e);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchLessonDetail = useCallback(async (lessonId) => {
    try {
      const res = await fetch(`${API_BASE}/lessons/${lessonId}`, { headers });
      const data = await res.json();
      if (data.success) {
        const gradeRes = await fetch(`${API_BASE}/lessons/${lessonId}/grade`, { headers });
        const gradeData = await gradeRes.json();
        setLessonData(prev => ({
          ...prev,
          [lessonId]: {
            videos: data.data.videos || [],
            grade: gradeData.success ? gradeData.data : null
          }
        }));
      }
    } catch (e) {
      console.error(e);
    }
  }, [token]);

  useEffect(() => {
    fetchLessons();
    const interval = setInterval(() => fetchLessons(true), 30000); // Refresh unlock state every 30s
    return () => clearInterval(interval);
  }, []);

  const toggleLesson = (lesson) => {
    const lessonId = lesson._id;
    const isLessonUnlocked = lesson.isUnlocked !== false;

    if (expandedLesson === lessonId) {
      setExpandedLesson(null);
      return;
    }

    setExpandedLesson(lessonId);
    if (isLessonUnlocked) {
      fetchLessonDetail(lessonId);
    }
  };

  const handleWatch = (video) => {
    if (!video.isUnlocked) return;
    // Support both legacy driveViewLink and new videoUrl field
    const url = video.videoUrl || video.driveViewLink;
    setPlayingVideo({ videoId: video._id, url, title: video.title });
  };

  const openRecorder = (video, lesson) => {
    setRecorderModal({ video, lesson });
  };

  const handleRecorderClose = () => {
    setRecorderModal(null);
  };

  const handleRecorderSuccess = () => {
    // Refresh all expanded lesson data after submission
    if (expandedLesson) fetchLessonDetail(expandedLesson);
  };

  const getVideoStatusClass = (video) => {
    if (!video.isUnlocked) return 'opacity-50 cursor-not-allowed bg-gray-50 border-gray-200';
    if (video.submission?.status === 'approved') return 'border-green-400 bg-green-50';
    if (video.submission?.status === 'pending') return 'border-yellow-400 bg-yellow-50';
    if (video.submission?.status === 'rejected') return 'border-red-400 bg-red-50';
    return 'border-gray-200 hover:border-orange-300 bg-white';
  };

  const submissionBadge = (sub) => {
    if (!sub) return null;
    const map = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold capitalize ${map[sub.status]}`}>
        {sub.status}
        {sub.status === 'approved' && sub.grade !== null ? ` – ${sub.grade}/10` : ''}
      </span>
    );
  };

  const canSubmit = (video) =>
    video.isUnlocked && (!video.submission || video.submission.status === 'rejected');

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-20 pb-8 transition-colors duration-500">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-8 bg-gradient-to-r from-orange-600 to-orange-700 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <FaBook className="text-2xl" />
            <h1 className="text-3xl font-bold">My Lessons</h1>
          </div>
          <p className="text-orange-100 text-sm">
            Watch each video, record your practice, and get graded by your teacher to unlock the next one.
          </p>
        </div>

        {loading && (
          <div className="text-center py-16 text-gray-500 dark:text-gray-400">Loading lessons...</div>
        )}

        {!loading && lessons.length === 0 && (
          <div className="text-center py-16 text-gray-400 dark:text-gray-500">
            <FaBook className="text-6xl mx-auto mb-4 opacity-20" />
            <p className="text-lg font-medium">No lessons available yet.</p>
            <p className="text-sm mt-1">Check back later!</p>
          </div>
        )}

        {/* Lessons Accordion */}
        <div className="space-y-4">
          {lessons.map((lesson, lIndex) => {
            const isExpanded = expandedLesson === lesson._id;
            const data = lessonData[lesson._id];
            const grade = data?.grade;
            const isLessonComplete = grade && (
              grade.isComplete !== undefined
                ? grade.isComplete
                : (grade.submissionsCount === grade.totalVideos && grade.totalVideos > 0)
            );
            const isLessonUnlocked = lesson.isUnlocked !== false;

            return (
              <div key={lesson._id} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors duration-500">
                {/* Lesson Header */}
                <div
                  onClick={() => toggleLesson(lesson)}
                  className={`w-full flex items-center justify-between p-5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition text-left cursor-pointer ${
                    !isLessonUnlocked ? 'opacity-60' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                        !isLessonUnlocked ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500' : isLessonComplete ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400' : 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-400'
                      }`}
                    >
                      {!isLessonUnlocked ? <FaLock className="text-xs" /> : isLessonComplete ? <FaCheckCircle /> : lIndex + 1}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-white text-lg">
                        {lesson.title}
                        {!isLessonUnlocked && <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 font-medium">(Locked)</span>}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">{lesson.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                    {!isLessonUnlocked && lesson.price > 0 && !lesson.hasPaid && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLessonForPayment(lesson);
                          setPaymentStatus(null);
                        }}
                        className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 rounded-full text-xs font-bold transition flex items-center gap-1.5 shadow-sm active:scale-95"
                      >
                        Unlock for ₹{lesson.price}
                      </button>
                    )}
                    {isLessonUnlocked && isLessonComplete && grade && (
                      <div className="flex items-center gap-1 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-500/30 px-3 py-1 rounded-full">
                        <FaStar className="text-orange-500 dark:text-orange-400 text-sm" />
                        <span className="text-orange-700 dark:text-orange-300 font-bold text-sm">{grade.totalGrade}/{grade.maxGrade}</span>
                      </div>
                    )}
                    {isExpanded ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                  </div>
                </div>

                {/* Completion Banner */}
                {isExpanded && isLessonComplete && grade && (
                  <div className="mx-5 mb-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border border-green-200 dark:border-green-500/30 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-800 dark:text-green-300">🎉 Lesson Complete!</p>
                      <p className="text-xs text-green-600 dark:text-green-400">You completed this lesson.</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-700 dark:text-green-300">{grade.totalGrade}<span className="text-sm font-medium text-green-500 dark:text-green-400/50">/{grade.maxGrade}</span></p>
                      <p className="text-xs text-green-600 dark:text-green-400">Total Score</p>
                    </div>
                  </div>
                )}

                {/* Videos List — expanded always now, but content differs if locked */}
                {isExpanded && (
                  <div className="px-5 pb-5">
                    {!isLessonUnlocked && (
                      <div className="text-center py-12 px-6 border-2 border-dashed border-gray-100 dark:border-gray-700 rounded-xl bg-gray-50/50 dark:bg-gray-800/50">
                        <FaLock className="text-3xl text-gray-200 dark:text-gray-600 mx-auto mb-4" />
                        <h4 className="text-gray-900 dark:text-white font-bold mb-1">Locked Course</h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
                          {lesson.price > 0 && !lesson.hasPaid 
                            ? "Complete previous lessons and pay the unlock fee above to access these videos."
                            : "Complete and get approval for all previous course videos to move forward."}
                        </p>
                      </div>
                    )}
                    {isLessonUnlocked && !data && <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">Loading videos...</div>}
                    {isLessonUnlocked && data && data.videos.length === 0 && (
                      <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">No videos in this lesson yet.</div>
                    )}
                    {data && data.videos.length > 0 && (
                      <div className="space-y-3">
                        {data.videos.map((video, vIndex) => (
                          <div
                            key={video._id}
                            className={`rounded-xl border-2 p-4 transition ${getVideoStatusClass(video)}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              {/* Left: icon + info */}
                              <div className="flex items-start gap-3 flex-1">
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold mt-0.5 ${
                                  video.submission?.status === 'approved'
                                    ? 'bg-green-100 text-green-700'
                                    : video.isUnlocked
                                    ? 'bg-orange-100 text-orange-700'
                                    : 'bg-gray-100 text-gray-400'
                                }`}>
                                  {video.submission?.status === 'approved'
                                    ? <FaCheckCircle />
                                    : video.isUnlocked
                                    ? vIndex + 1
                                    : <FaLock className="text-xs" />
                                  }
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className={`font-semibold ${!video.isUnlocked ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>
                                      {video.title}
                                    </p>
                                    {!video.isUnlocked && (
                                      <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                                        <FaLock className="text-xs" /> Locked
                                      </span>
                                    )}
                                    {submissionBadge(video.submission)}
                                  </div>
                                  {video.description && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{video.description}</p>
                                  )}
                                  {video.homework && (
                                    <div className="mt-2 text-sm bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30 rounded-lg p-3 text-blue-900 dark:text-blue-100">
                                      <p className="font-semibold flex items-center gap-1.5 mb-1"><FaBook className="text-blue-600 dark:text-blue-400" /> Homework assigned:</p>
                                      <p className="whitespace-pre-line text-blue-800 dark:text-blue-200">{video.homework}</p>
                                    </div>
                                  )}
                                  {video.submission?.feedback && (
                                    <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 italic bg-gray-100 dark:bg-gray-700/50 rounded px-2 py-1">
                                      Teacher: "{video.submission.feedback}"
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Right: action buttons */}
                              {video.isUnlocked && (
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {/* Watch */}
                                  <button
                                    onClick={() => handleWatch(video)}
                                    className="flex items-center gap-1.5 px-3 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition"
                                  >
                                    <FaPlayCircle className="text-sm" /> Watch
                                  </button>

                                  {/* Submit Practice — always shown when unlocked & no pending/approved */}
                                  {canSubmit(video) && (
                                    <button
                                      onClick={() => openRecorder(video, lesson)}
                                      className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition"
                                    >
                                      <FaVideo className="text-sm" /> Submit
                                    </button>
                                  )}

                                  {/* Pending — show pill only */}
                                  {video.submission?.status === 'pending' && (
                                    <span className="text-xs px-3 py-2 bg-yellow-100 text-yellow-700 rounded-lg font-medium">
                                      ⏳ Pending review
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Video Player Modal */}
      {playingVideo && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div id="video-modal-wrapper" className="bg-black rounded-2xl overflow-hidden w-full max-w-4xl shadow-2xl relative">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-900 absolute top-0 left-0 right-0 z-10 opacity-0 hover:opacity-100 transition-opacity">
              <p className="text-white font-medium text-sm truncate">{playingVideo.title}</p>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => {
                    const wrapper = document.getElementById('video-modal-wrapper');
                    if (!document.fullscreenElement) {
                      wrapper.requestFullscreen().catch(err => console.log(err));
                    } else {
                      document.exitFullscreen();
                    }
                  }}
                  className="text-gray-400 hover:text-white transition text-lg"
                  title="Fullscreen"
                >
                  <FaBook className="hidden" /> {/* just importing FaExpand would be better but I can use an SVG or text */}
                  ⛶
                </button>
                <button
                  onClick={() => {
                    setPlayingVideo(null);
                    if (expandedLesson) fetchLessonDetail(expandedLesson);
                  }}
                  className="text-gray-400 hover:text-white transition text-xl font-bold"
                >
                  ✕
                </button>
              </div>
            </div>
            {/* YouTube embed — overflow:hidden clips bottom controls bar */}
            <div style={{ position: 'relative', width: '100%', paddingBottom: '50%', overflow: 'hidden', background: '#000' }}>
              {playingVideo.url && (
                playingVideo.url.includes('youtube.com/embed') || playingVideo.url.includes('drive.google.com') ? (
                  <iframe
                    src={(() => {
                      try {
                        const u = new URL(playingVideo.url);
                        u.searchParams.set('rel', '0');
                        u.searchParams.set('modestbranding', '1');
                        return u.toString();
                      } catch { return playingVideo.url; }
                    })()}
                    title={playingVideo.title}
                    style={{
                      position: 'absolute',
                      top: '0',
                      left: '0',
                      width: '100%',
                      height: '120%',
                      border: 'none',
                      pointerEvents: 'auto'
                    }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  />
                ) : (
                  <video
                    src={playingVideo.url}
                    controls
                    autoPlay
                    controlsList="nodownload noplaybackrate nofullscreen"
                    disablePictureInPicture
                    onContextMenu={(e) => e.preventDefault()}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                )
              )}
            </div>
            <div className="flex items-center justify-between px-4 py-3 bg-gray-900">
              <p className="text-gray-400 text-xs">
                After watching, click <strong className="text-white">Submit</strong> on the lesson card to upload your practice video.
              </p>
              <button
                onClick={() => setPlayingVideo(null)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition"
              >
                <FaUpload /> Done Watching
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Recorder / Submission Modal */}
      {recorderModal && (
        <VideoRecorder
          video={recorderModal.video}
          lesson={recorderModal.lesson}
          onClose={handleRecorderClose}
          onSuccess={handleRecorderSuccess}
        />
      )}

      {/* Course Payment Modal (QR Code) */}
      {selectedLessonForPayment && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl overflow-hidden w-full max-w-md shadow-2xl animate-fade-in-up">
            <div className="bg-orange-600 px-6 py-4 flex items-center justify-between text-white">
              <h3 className="font-bold text-lg">Unlock Course</h3>
              <button 
                onClick={() => setSelectedLessonForPayment(null)} 
                className="hover:rotate-90 transition-transform text-xl"
              >✕</button>
            </div>
            
            <div className="p-6">
              <div className="text-center mb-6">
                <p className="text-gray-600 text-sm mb-1 uppercase tracking-wider font-semibold">You're unlocking</p>
                <h4 className="text-xl font-bold text-gray-900">{selectedLessonForPayment.title}</h4>
                <div className="inline-block bg-orange-100 text-orange-700 px-4 py-1 rounded-full text-lg font-bold mt-2">
                  ₹{selectedLessonForPayment.price}
                </div>
              </div>

              {!paymentStatus?.success ? (
                <>
                  <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-6 text-center">
                    <p className="text-sm text-gray-600 mb-4 font-medium uppercase text-xs tracking-widest">Scan to Pay via UPI</p>
                    <div className="bg-white p-2 rounded-xl shadow-inner inline-block border border-gray-100 mb-4">
                      <img 
                        src="/upi-qr.png" 
                        alt="UPI QR Code" 
                        className="w-48 h-48 object-contain"
                        onError={(e) => {
                          e.target.onerror = null; 
                          e.target.src = "https://via.placeholder.com/200?text=QR+Code+Missing";
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 italic">Scan with GPay, PhonePe, or ANY UPI app</p>
                  </div>

                  <form onSubmit={handleLessonPaymentSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        UTR / Reference Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={utr}
                        onChange={(e) => setUtr(e.target.value)}
                        placeholder="Enter 12-digit UTR/Ref"
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-100 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all font-mono"
                        required
                      />
                      <p className="text-[10px] text-gray-400 mt-1 uppercase tracking-tighter">Enter the ID from your payment app success screen</p>
                    </div>

                    {paymentStatus?.success === false && (
                      <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm font-medium border border-red-100">
                        {paymentStatus.message}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={paymentLoading || utr.trim().length < 5}
                      className={`w-full py-4 rounded-xl font-bold transition-all ${
                        paymentLoading || utr.trim().length < 5
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-orange-600 hover:bg-orange-700 text-white shadow-lg active:scale-95'
                      }`}
                    >
                      {paymentLoading ? 'Submitting...' : 'Submit Payment Reference'}
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 scale-in">
                    <FaCheckCircle className="text-4xl text-green-600" />
                  </div>
                  <h5 className="text-xl font-bold text-gray-900 mb-2">Submission Received!</h5>
                  <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                    Our admin will verify your payment (Ref: <span className="font-mono text-orange-600">{utr}</span>) and unlock <strong>{selectedLessonForPayment.title}</strong> for you shortly.
                  </p>
                  <button
                    onClick={() => setSelectedLessonForPayment(null)}
                    className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition-all"
                  >
                    Close Window
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonsPage;
